import { useEffect, useRef, useState } from 'react'
import { X } from 'lucide-react'
import { inlineInput } from '@/lib/form-styles'
import { lookupAddresses, lookupClients, type PartnerAdresse } from '../api/projects-head.api'
import type { LookupUser } from '../types/project.types'

/**
 * AP-35 Nachtrag 2 (Auftraggeber 18.9.2026) — **Auswahlfelder** der Projektmasken.
 *
 * - `KundenSuche`: ein Suchfeld mit Vorschlagsliste. Vorher stand ein Suchfeld
 *   über einem Dropdown; die Treffer landeten unsichtbar im Dropdown, und ohne
 *   CRM-Recht kam gar keiner (die Suche lief über `/clients`).
 * - `AdressSuche`: jede Adresse **aller** Geschäftspartner, mit Firmenname.
 * - `RollenBenutzer`: Mitarbeiter mit passender Projektrolle aus der
 *   Mitarbeiterverwaltung; hat niemand die Rolle, alle.
 *
 * Vorschlagslisten nach `.claude/rules/styling.md` (Hintergrund direkt am Element).
 */

const feld = inlineInput
const LISTE = 'absolute left-0 top-full z-50 mt-1 max-h-64 w-full min-w-[18rem] overflow-auto rounded-md border border-border shadow-lg'
const listenStil = { background: 'hsl(var(--card))', color: 'hsl(var(--card-foreground))' }

function useVerzoegert<T>(text: string, aktiv: boolean, suche: (q: string) => Promise<T[]>, mindestens = 1) {
  const [treffer, setTreffer] = useState<T[]>([])
  const ref = useRef(suche)
  ref.current = suche
  useEffect(() => {
    if (!aktiv || text.trim().length < mindestens) {
      setTreffer([])
      return
    }
    const t = setTimeout(() => ref.current(text.trim()).then(setTreffer).catch(() => setTreffer([])), 250)
    return () => clearTimeout(t)
  }, [text, aktiv, mindestens])
  return [treffer, setTreffer] as const
}

// ── Kunde ───────────────────────────────────────────────────────────────────

export function KundenSuche({
  wert,
  onWahl,
  autoFocus,
  allePartner,
  platzhalter = 'Kunden suchen — Name oder Nummer',
  deaktiviert,
}: {
  wert: { id: string; name: string } | null
  onWahl: (k: { id: string; name: string } | null) => void
  autoFocus?: boolean
  /** jeder Geschäftspartner statt nur Kunden (Statik/Planung, Nachtrag 4) */
  allePartner?: boolean
  platzhalter?: string
  deaktiviert?: boolean
}) {
  const [text, setText] = useState(wert?.name ?? '')
  const [offen, setOffen] = useState(false)
  const [treffer, setTreffer] = useVerzoegert(text, offen, (q) => lookupClients(q, allePartner))

  useEffect(() => setText(wert?.name ?? ''), [wert?.id, wert?.name])

  return (
    <div className="relative">
      <input
        className={`${feld} pr-7`}
        autoFocus={autoFocus}
        placeholder={platzhalter}
        disabled={deaktiviert}
        value={text}
        onFocus={() => setOffen(true)}
        onBlur={() => {
          setOffen(false)
          // Getippt, aber nicht gewählt: der gewählte Kunde bleibt stehen.
          setText(wert?.name ?? '')
        }}
        onChange={(e) => {
          setText(e.target.value)
          setOffen(true)
        }}
      />
      {wert && !deaktiviert && (
        <button
          type="button"
          tabIndex={-1}
          className="absolute right-1.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
          aria-label="Kunde entfernen"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => onWahl(null)}
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}
      {offen && treffer.length > 0 && (
        // `onMouseDown`: Ein Druck auf den Rollbalken der Liste nimmt dem Feld den Fokus nicht — sie ginge sonst zu.
        <div className={LISTE} style={listenStil} onMouseDown={(e) => e.preventDefault()}>
          {treffer.map((k) => (
            <button
              key={k.id}
              type="button"
              tabIndex={-1}
              className="block w-full px-2 py-1.5 text-left text-sm hover:bg-muted"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                onWahl({ id: k.id, name: k.name })
                setText(k.name)
                setOffen(false)
                setTreffer([])
              }}
            >
              {k.name} <span className="text-xs text-muted-foreground">{k.clientNumber}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

// ── Adresse ────────────────────────────────────────────────────────────────

export interface AdressEingabe {
  modus: 'none' | 'partner' | 'own'
  addressId: string
  /** Anzeige der gewählten Partneradresse. */
  anzeige: string
  recipient: string
  street: string
  addition: string
  zip: string
  city: string
  country: string
}

export const leereAdresse = (): AdressEingabe => ({
  modus: 'none',
  addressId: '',
  anzeige: '',
  recipient: '',
  street: '',
  addition: '',
  zip: '',
  city: '',
  country: 'AT',
})

/** Firma, Empfänger, Straße, PLZ Ort — eine Zeile. */
export function adressZeile(a: Partial<PartnerAdresse> & Record<string, unknown>, firma?: string | null): string {
  const f = firma ?? (a.client as { name?: string } | null | undefined)?.name ?? null
  return [f, a.recipient && a.recipient !== f ? a.recipient : null, a.street, a.addition, [a.zip, a.city].filter(Boolean).join(' ')]
    .filter(Boolean)
    .join(', ')
}

/** Nach dem Speichern: Verweis → `addressId`, frei → `addressJson`, sonst beides leer. */
export function alsNutzlast(a: AdressEingabe): { addressId: string | null; addressJson: Record<string, string> | null } {
  if (a.modus === 'partner' && a.addressId) return { addressId: a.addressId, addressJson: null }
  if (a.modus === 'own') {
    const frei = Object.fromEntries(
      (['recipient', 'street', 'addition', 'zip', 'city', 'country'] as const).map((k) => [k, a[k].trim()]).filter(([, v]) => v !== ''),
    )
    return { addressId: null, addressJson: Object.keys(frei).length ? frei : null }
  }
  return { addressId: null, addressJson: null }
}

/**
 * Eine Adresse wählen: aus den Geschäftspartnern suchen **oder** in die Felder
 * tippen. Beides zugleich gibt es nicht (Blatt 1 2.4).
 */
export function AdressSuche({ wert, onChange }: { wert: AdressEingabe; onChange: (a: AdressEingabe) => void }) {
  const [text, setText] = useState('')
  const [offen, setOffen] = useState(false)
  const [treffer, setTreffer] = useVerzoegert(text, offen, lookupAddresses, 2)
  const setze = (t: Partial<AdressEingabe>) => onChange({ ...wert, ...t })

  return (
    <div className="space-y-1">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-[16rem] flex-1">
          <input
            className={feld}
            placeholder={wert.modus === 'partner' ? wert.anzeige : 'Geschäftspartner suchen — Firma, Straße, Ort, PLZ'}
            value={text}
            onFocus={() => setOffen(true)}
            onBlur={() => setOffen(false)}
            onChange={(e) => {
              setText(e.target.value)
              setOffen(true)
            }}
          />
          {offen && treffer.length > 0 && (
            <div className={LISTE} style={listenStil} onMouseDown={(e) => e.preventDefault()}>
              {treffer.map((a) => (
                <button
                  key={a.id}
                  type="button"
                  tabIndex={-1}
                  className="block w-full px-2 py-1.5 text-left text-sm hover:bg-muted"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    setze({ modus: 'partner', addressId: a.id, anzeige: adressZeile(a as never) })
                    setText('')
                    setOffen(false)
                    setTreffer([])
                  }}
                >
                  <span className="font-medium">{a.client?.name ?? '—'}</span>
                  <span className="block text-xs text-muted-foreground">{adressZeile({ ...a, client: null } as never)}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        <button
          type="button"
          className={`rounded-md border px-2 py-1 text-xs ${wert.modus === 'own' ? 'border-primary bg-primary/10' : 'border-border'}`}
          onClick={() => setze({ modus: 'own' })}
        >
          eintippen
        </button>
        {wert.modus !== 'none' && (
          <button type="button" className="text-xs text-muted-foreground underline" onClick={() => onChange(leereAdresse())}>
            leeren
          </button>
        )}
      </div>
      {wert.modus === 'partner' && <p className="text-sm font-medium">{wert.anzeige}</p>}
      {wert.modus === 'own' && (
        <div className="grid gap-1 sm:grid-cols-6">
          <input className={`${feld} sm:col-span-6`} placeholder="Firma / Empfänger" value={wert.recipient} onChange={(e) => setze({ recipient: e.target.value })} />
          <input className={`${feld} sm:col-span-4`} placeholder="Straße" value={wert.street} onChange={(e) => setze({ street: e.target.value })} />
          <input className={`${feld} sm:col-span-2`} placeholder="Zusatz" value={wert.addition} onChange={(e) => setze({ addition: e.target.value })} />
          <input className={`${feld} sm:col-span-1`} placeholder="PLZ" value={wert.zip} onChange={(e) => setze({ zip: e.target.value })} />
          <input className={`${feld} sm:col-span-4`} placeholder="Ort" value={wert.city} onChange={(e) => setze({ city: e.target.value })} />
          <input className={`${feld} sm:col-span-1`} placeholder="Land" maxLength={2} value={wert.country} onChange={(e) => setze({ country: e.target.value.toUpperCase() })} />
        </div>
      )}
    </div>
  )
}

// ── Mitarbeiter nach Projektrolle ──────────────────────────────────────────

/** Wer für diese Rolle in Frage kommt — hat niemand die Rolle, alle (Nachtrag 2). */
export function kandidaten(benutzer: LookupUser[], rolle: string | null | undefined): LookupUser[] {
  if (!rolle) return benutzer
  const passend = benutzer.filter((b) => b.projectRoles?.includes(rolle))
  return passend.length ? passend : benutzer
}

export function RollenBenutzer({
  benutzer,
  rolle,
  wert,
  onChange,
  leer = '— niemand —',
  className,
}: {
  benutzer: LookupUser[]
  rolle?: string | null
  wert: string
  onChange: (id: string) => void
  leer?: string
  className?: string
}) {
  const liste = kandidaten(benutzer, rolle)
  // Wer schon eingetragen ist, bleibt wählbar — auch wenn er die Rolle inzwischen nicht mehr hat.
  const fehlt = wert && !liste.some((b) => b.id === wert) ? benutzer.find((b) => b.id === wert) : null
  return (
    <select className={className ?? feld} value={wert} onChange={(e) => onChange(e.target.value)}>
      <option value="">{leer}</option>
      {fehlt && (
        <option value={fehlt.id} className="text-foreground">
          {fehlt.name}
        </option>
      )}
      {liste.map((b) => (
        <option key={b.id} value={b.id} className="text-foreground">
          {b.name}
        </option>
      ))}
    </select>
  )
}
